import { useSigma } from "@react-sigma/core";
import { FC, PropsWithChildren, useEffect } from "react";
import { Dataset, FiltersState } from "../types";

const COMPANY_CLUSTER_COLOR = "#333333"; // Light black color for company clusters

const GraphDataController: FC<PropsWithChildren<{ dataset: Dataset; filters: FiltersState }>> = ({
  dataset,
  filters,
  children,
}) => {
  const sigma = useSigma();
  const graph = sigma.getGraph();

  useEffect(() => {
    if (!graph || !dataset) return;

    console.log("Initializing graph with dataset:", dataset);

    graph.clear();

    dataset.nodes.forEach((node) => {
      const topicCluster = dataset.topicClusters.find(c => c.key === node.topicCluster[0]);
      graph.addNode(node.id, {
        ...node,
        x: node.x,
        y: node.y,
        size: node.companyTags.length,
        color: topicCluster ? topicCluster.color : COMPANY_CLUSTER_COLOR,
      });
    });

    dataset.edges.forEach(({ source, target }) => graph.addEdge(source, target, { size: 1 }));

    // Normalize node sizes
    const sizes = graph.nodes().map((node) => graph.getNodeAttribute(node, "size"));
    const minSize = Math.min(...sizes);
    const maxSize = Math.max(...sizes);
    const MIN_NODE_SIZE = 3;
    const MAX_NODE_SIZE = 30;

    graph.forEachNode((node) => {
      const normalizedSize = ((graph.getNodeAttribute(node, "size") - minSize) / (maxSize - minSize)) * 
        (MAX_NODE_SIZE - MIN_NODE_SIZE) + MIN_NODE_SIZE;
      graph.setNodeAttribute(node, "size", normalizedSize);
    });

    console.log("Graph initialized with nodes:", graph.order, "and edges:", graph.size);

    sigma.refresh();

    return () => graph.clear();
  }, [graph, dataset]);

  useEffect(() => {
    if (!graph) return;

    console.log("Applying filters:", filters);

    const { clusters } = filters;
    const activeTopicClusters = Object.keys(clusters).filter(key => key.startsWith('topic-') && clusters[key]);
    const activeCompanyClusters = Object.keys(clusters).filter(key => key.startsWith('company-') && clusters[key]);

    const allTopicClusters = Object.keys(clusters).filter(key => key.startsWith('topic-'));
    const allCompanyClusters = Object.keys(clusters).filter(key => key.startsWith('company-'));

    graph.forEachNode((node, attributes) => {
      const isTopicVisible = activeTopicClusters.length > 0 && attributes.topicCluster.some((cluster: string) => clusters[`topic-${cluster}`]);
      const isCompanyVisible = activeCompanyClusters.length === 0 || attributes.companyCluster.some((cluster: string) => clusters[`company-${cluster}`]);
      const isVisible = isTopicVisible && isCompanyVisible;
      graph.setNodeAttribute(node, "hidden", !isVisible);
    });

    graph.forEachEdge((edge, attributes, source, target) => {
      const isVisible = !graph.getNodeAttribute(source, "hidden") && !graph.getNodeAttribute(target, "hidden");
      graph.setEdgeAttribute(edge, "hidden", !isVisible);
    });

    sigma.refresh();
  }, [graph, filters]);

  return <>{children}</>;
};

export default GraphDataController;
